import { NULL_AS_STRING } from '@repo/shared/dtos/beneficiary'
import { BooleanSchemaItem } from '@repo/shared/dtos/structured-data'
import { Button } from '@repo/ui/button'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useT } from '@repo/ui/hooks'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { Trash2Icon } from 'lucide-react'
import { useMemo } from 'react'

import { BaseForm } from '@/components/admin/structure-data/schema/forms/BaseForm'
import {
  BooleanFormValues,
  booleanNullToString,
  booleanValidationSchema,
  stringToBooleanNull,
} from '@/components/admin/structure-data/schema/forms/validation'

export function ItemBooleanForm({
  item,
  onUpdate,
}: {
  item: BooleanSchemaItem
  onUpdate: (item: BooleanSchemaItem) => void
}) {
  const { tc } = useT()

  const defaultValues = useMemo(
    () =>
      ({
        label: item.label,
        instructionsToFillValue: item.instructionsToFillValue,
        defaultValue: booleanNullToString(item.defaultValue),
        type: 'boolean',
      }) satisfies BooleanFormValues,
    [item],
  )

  const handleUpdate = (values: BooleanFormValues) => {
    onUpdate({
      ...item,
      ...values,
      defaultValue: stringToBooleanNull(values.defaultValue),
    })
  }

  return (
    <BaseForm
      defaultValues={defaultValues}
      onUpdate={handleUpdate}
      schema={booleanValidationSchema}
    >
      {(formApi) => (
        <FormField
          control={formApi.control}
          name="defaultValue"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {tc('defaultValue', { cfl: true })} ({tc('optional')})
              </FormLabel>
              <FormControl>
                <div className="flex items-center gap-2">
                  <Select
                    name={field.name}
                    onValueChange={(value) => field.onChange(value, { shouldDirty: true })}
                    value={field.value}
                    defaultValue={NULL_AS_STRING}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={tc('select', { cfl: true })} />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value={NULL_AS_STRING}>
                        {tc(NULL_AS_STRING, { cfl: true })}
                      </SelectItem>
                      <SelectItem value="true">{tc('true', { cfl: true })}</SelectItem>
                      <SelectItem value="false">{tc('false', { cfl: true })}</SelectItem>
                    </SelectContent>
                  </Select>
                  <TooltipWrapper content={tc('delete', { cfl: true })}>
                    <Button
                      variant="outline"
                      onClick={() => field.onChange(NULL_AS_STRING, { shouldDirty: true })}
                      size="icon"
                      disabled={field.value === NULL_AS_STRING}
                    >
                      <Trash2Icon className="size-4" />
                    </Button>
                  </TooltipWrapper>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </BaseForm>
  )
}
